import { useEffect, useRef } from "react";
import {
  NODE_LAYOUT,
  NODE_META,
  ADJACENCY,
  LEAD_NEXT,
  TEAM_INDICES,
} from "@/lib/agentNetwork";

/**
 * QuantumLightfield — Team Frames + Relay v6
 * -------------------------------------------------------------
 *  Four big hexagonal TEAM frames (one per team of 5 agents) +
 *  ambient holographic field + cyclic relay packets.
 *  No central hub, no static spider-web edges: lines only exist
 *  as the bright trail the packets draw while in flight.
 */

const buildPerlin = () => {
  const permutation = new Uint8Array(256);
  for (let i = 0; i < 256; i++) permutation[i] = i;
  let seed = 137;
  for (let i = 255; i > 0; i--) {
    seed = (seed * 16807) % 2147483647;
    const j = seed % (i + 1);
    [permutation[i], permutation[j]] = [permutation[j], permutation[i]];
  }
  const p = new Uint8Array(512);
  for (let i = 0; i < 512; i++) p[i] = permutation[i & 255];
  const fade = (t) => t * t * t * (t * (t * 6 - 15) + 10);
  const lerp = (a, b, t) => a + t * (b - a);
  const grad = (hash, x, y) => {
    const h = hash & 3;
    const u = h < 2 ? x : y;
    const v = h < 2 ? y : x;
    return ((h & 1) === 0 ? u : -u) + ((h & 2) === 0 ? v : -v);
  };
  return (x, y) => {
    const X = Math.floor(x) & 255;
    const Y = Math.floor(y) & 255;
    x -= Math.floor(x);
    y -= Math.floor(y);
    const u = fade(x);
    const v = fade(y);
    const A = p[X] + Y;
    const B = p[X + 1] + Y;
    return lerp(
      lerp(grad(p[A], x, y), grad(p[B], x - 1, y), u),
      lerp(grad(p[A + 1], x, y - 1), grad(p[B + 1], x - 1, y - 1), u),
      v
    );
  };
};

const easeInOut = (t) => (t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2);

const NODE_PAD = 26;
const TEAM_HOP_MIN = 3;
const TEAM_HOP_MAX = 5;
const MAX_PACKETS = 10;

const QuantumLightfield = () => {
  const canvasRef = useRef(null);
  const rafRef = useRef(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d", { alpha: false });
    const noise2D = buildPerlin();

    let width = 0;
    let height = 0;
    let dpr = Math.min(window.devicePixelRatio || 1, 2);

    const pointer = { x: 0, y: 0, sx: 0, sy: 0, active: false };

    let streams = [];
    let blobs = [];
    let nodes = [];
    let packets = [];

    const newTarget = () =>
      TEAM_HOP_MIN + Math.floor(Math.random() * (TEAM_HOP_MAX - TEAM_HOP_MIN + 1));

    const spawnStream = (initial = false) => {
      const maxLife = 600 + Math.random() * 400;
      return {
        x: Math.random() * width,
        y: Math.random() * height,
        px: 0, py: 0, vx: 0, vy: 0,
        life: initial ? Math.random() * maxLife : 0,
        maxLife,
        alpha: 0.018 + Math.random() * 0.014,
      };
    };

    const spawnBlob = () => {
      const cx = width / 2, cy = height / 2;
      const ang = Math.random() * Math.PI * 2;
      const dist = Math.random() * Math.min(width, height) * 0.45;
      return {
        x: cx + Math.cos(ang) * dist,
        y: cy + Math.sin(ang) * dist,
        vx: (Math.random() - 0.5) * 0.18,
        vy: (Math.random() - 0.5) * 0.18,
        radius: Math.min(width, height) * (0.2 + Math.random() * 0.18),
        hue: Math.random() < 0.88 ? "cyan" : "amber",
        phase: Math.random() * Math.PI * 2,
      };
    };

    // --- Packet routing --------------------------------------------
    const buildHopFromIntra = (fromIdx, prevIdx, teamHops) => {
      const neigh = ADJACENCY[fromIdx];
      if (!neigh || neigh.length === 0) return null;
      let candidates = neigh.filter((n) => n !== prevIdx);
      if (candidates.length === 0) candidates = neigh;
      const toIdx = candidates[Math.floor(Math.random() * candidates.length)];
      return {
        fromIdx,
        toIdx,
        t: 0,
        speed: 0.006 + Math.random() * 0.005,
        teamHops,
        target: newTarget(),
        prevIdx,
        isHandoff: false,
      };
    };

    const buildHopFromLead = (leadIdx) => {
      const dest = LEAD_NEXT[leadIdx];
      if (dest == null) return null;
      return {
        fromIdx: leadIdx,
        toIdx: dest,
        t: 0,
        speed: 0.0055,
        teamHops: 0,
        target: newTarget(),
        prevIdx: leadIdx,
        isHandoff: true,
      };
    };

    const decideNext = (p) => {
      const arrived = p.toIdx;
      const meta = NODE_META[arrived];

      // At a LEAD with enough hops — hand off to next team's lead
      if (meta.isLead && p.teamHops >= p.target) {
        const hop = buildHopFromLead(arrived);
        if (hop) return hop;
      }
      // Continue intra-team work
      return buildHopFromIntra(arrived, p.fromIdx, p.teamHops + 1);
    };

    const buildNetwork = () => {
      nodes = NODE_LAYOUT.map(([nx, ny]) => ({
        x: nx * width,
        y: ny * height,
        flashUntil: 0,
        pulse: Math.random() * Math.PI * 2,
      }));

      packets = [];
      // Seed packets spread across all four teams for parallel activity
      const seeds = [
        ...TEAM_INDICES[0].slice(0, 2),
        ...TEAM_INDICES[1].slice(0, 2),
        ...TEAM_INDICES[2].slice(0, 2),
        ...TEAM_INDICES[3].slice(0, 2),
        TEAM_INDICES[0][3], TEAM_INDICES[2][3],
      ];
      seeds.forEach((idx) => {
        const p = buildHopFromIntra(idx, -1, Math.floor(Math.random() * 3));
        if (p) {
          p.t = Math.random() * 0.6;
          packets.push(p);
        }
      });
      while (packets.length < MAX_PACKETS) {
        const idx = TEAM_INDICES[0][Math.floor(Math.random() * 4)];
        const p = buildHopFromIntra(idx, -1, 0);
        if (!p) break;
        p.t = Math.random() * 0.8;
        packets.push(p);
      }
    };

    const resize = () => {
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      width = canvas.clientWidth;
      height = canvas.clientHeight;
      canvas.width = Math.floor(width * dpr);
      canvas.height = Math.floor(height * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      if (!pointer.active) {
        pointer.x = pointer.sx = width / 2;
        pointer.y = pointer.sy = height / 2;
      }

      const streamCount = Math.max(10, Math.min(18, Math.round((width * height) / 130000)));
      streams = new Array(streamCount).fill(0).map(() => spawnStream(true));
      blobs = new Array(6).fill(0).map(() => spawnBlob());
      buildNetwork();

      ctx.globalCompositeOperation = "source-over";
      ctx.fillStyle = "#112332";
      ctx.fillRect(0, 0, width, height);
    };

    // --- Frame loop -------------------------------------------------
    let t = 0;
    const noiseScale = 0.0017;
    const speed = 0.35;

    const fieldAngle = (x, y) => {
      const n = noise2D(x * noiseScale, y * noiseScale + t * 0.0006);
      const n2 = noise2D(x * noiseScale * 2.1 + 100, y * noiseScale * 2.1 - 100);
      return (n + n2 * 0.35) * Math.PI * 2;
    };

    const applyCursorWarp = (x, y, vx, vy) => {
      const dx = x - pointer.sx;
      const dy = y - pointer.sy;
      const dist2 = dx * dx + dy * dy;
      const r2 = 260 * 260;
      if (dist2 < r2) {
        const fall = 1 - dist2 / r2;
        const pull = fall * fall;
        return [
          vx + (-dy * 0.0006 - dx * 0.00018) * pull * 80,
          vy + (dx * 0.0006 - dy * 0.00018) * pull * 80,
        ];
      }
      return [vx, vy];
    };

    const drawBlobs = () => {
      ctx.save();
      ctx.globalCompositeOperation = "lighter";
      for (const b of blobs) {
        b.phase += 0.003;
        b.x += b.vx + Math.sin(b.phase) * 0.18;
        b.y += b.vy + Math.cos(b.phase * 0.7) * 0.18;
        if (b.x < -b.radius) b.x = width + b.radius;
        if (b.x > width + b.radius) b.x = -b.radius;
        if (b.y < -b.radius) b.y = height + b.radius;
        if (b.y > height + b.radius) b.y = -b.radius;
        const grd = ctx.createRadialGradient(b.x, b.y, 0, b.x, b.y, b.radius);
        if (b.hue === "amber") {
          grd.addColorStop(0, "rgba(255, 179, 0, 0.04)");
          grd.addColorStop(1, "rgba(255, 179, 0, 0)");
        } else {
          grd.addColorStop(0, "rgba(0, 229, 255, 0.08)");
          grd.addColorStop(0.55, "rgba(0, 229, 255, 0.02)");
          grd.addColorStop(1, "rgba(0, 229, 255, 0)");
        }
        ctx.fillStyle = grd;
        ctx.beginPath();
        ctx.arc(b.x, b.y, b.radius, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.restore();
    };

    const drawHUDRings = () => {
      const cx = width / 2;
      const cy = height / 2;
      const baseR = Math.min(width, height) * 0.18;
      const rings = [
        { r: baseR * 1.0, speed: 0.001, dash: [6, 16], ticks: 60, alpha: 0.1 },
        { r: baseR * 1.55, speed: -0.0007, dash: [3, 28], ticks: 90, alpha: 0.07 },
        { r: baseR * 2.25, speed: 0.00045, dash: [2, 40], ticks: 120, alpha: 0.05 },
      ];
      ctx.save();
      ctx.globalCompositeOperation = "lighter";
      rings.forEach((ring) => {
        const rot = t * ring.speed;
        ctx.save();
        ctx.translate(cx, cy);
        ctx.rotate(rot);
        ctx.strokeStyle = `rgba(0, 229, 255, ${ring.alpha})`;
        ctx.lineWidth = 1;
        ctx.setLineDash(ring.dash);
        ctx.beginPath();
        ctx.arc(0, 0, ring.r, -Math.PI * 0.65, Math.PI * 1.35);
        ctx.stroke();
        ctx.setLineDash([]);
        ctx.strokeStyle = `rgba(0, 229, 255, ${ring.alpha * 1.5})`;
        ctx.lineWidth = 0.6;
        for (let i = 0; i < ring.ticks; i++) {
          const a = (i / ring.ticks) * Math.PI * 2;
          const inner = ring.r - (i % 5 === 0 ? 5 : 2.5);
          const outer = ring.r + (i % 5 === 0 ? 5 : 2.5);
          ctx.beginPath();
          ctx.moveTo(Math.cos(a) * inner, Math.sin(a) * inner);
          ctx.lineTo(Math.cos(a) * outer, Math.sin(a) * outer);
          ctx.stroke();
        }
        ctx.restore();
      });
      ctx.restore();
    };

    const drawStreams = () => {
      ctx.save();
      ctx.globalCompositeOperation = "lighter";
      ctx.lineCap = "round";
      ctx.lineWidth = 0.9;
      for (let i = 0; i < streams.length; i++) {
        const s = streams[i];
        const ang = fieldAngle(s.x, s.y);
        let tvx = Math.cos(ang) * speed;
        let tvy = Math.sin(ang) * speed;
        [tvx, tvy] = applyCursorWarp(s.x, s.y, tvx, tvy);
        s.vx += (tvx - s.vx) * 0.08;
        s.vy += (tvy - s.vy) * 0.08;
        s.px = s.x; s.py = s.y;
        s.x += s.vx; s.y += s.vy;
        ctx.strokeStyle = `rgba(0, 229, 255, ${s.alpha})`;
        ctx.beginPath();
        ctx.moveTo(s.px, s.py);
        ctx.lineTo(s.x, s.y);
        ctx.stroke();
        s.life++;
        const off = s.x < -40 || s.x > width + 40 || s.y < -40 || s.y > height + 40;
        if (s.life > s.maxLife || off) streams[i] = spawnStream(false);
      }
      ctx.restore();
    };

    // === AGENT NETWORK =============================================
    const drawNetwork = () => {
      ctx.save();
      ctx.globalCompositeOperation = "lighter";

      // 1) Soft node halos + flash bursts on arrival
      for (let i = 0; i < nodes.length; i++) {
        const n = nodes[i];
        n.pulse += 0.022;
        const breathe = 0.5 + 0.5 * Math.sin(n.pulse);
        const flashK = n.flashUntil > t ? Math.max(0, (n.flashUntil - t) / 38) : 0;
        const intensity = 0.18 + 0.08 * breathe + 0.4 * flashK;
        const haloR = 22 + flashK * 16;
        const halo = ctx.createRadialGradient(n.x, n.y, 0, n.x, n.y, haloR);
        halo.addColorStop(0, `rgba(0, 229, 255, ${intensity})`);
        halo.addColorStop(0.45, `rgba(0, 229, 255, ${intensity * 0.28})`);
        halo.addColorStop(1, "rgba(0, 229, 255, 0)");
        ctx.fillStyle = halo;
        ctx.beginPath();
        ctx.arc(n.x, n.y, haloR, 0, Math.PI * 2);
        ctx.fill();
      }

      // 2) Packets — each draws its own line as it travels
      for (let i = packets.length - 1; i >= 0; i--) {
        const p = packets[i];
        p.t += p.speed;
        const from = nodes[p.fromIdx];
        const to = nodes[p.toIdx];

        if (p.t >= 1) {
          to.flashUntil = t + (p.isHandoff ? 52 : 38);
          const next = decideNext(p);
          if (next) packets[i] = next;
          else packets.splice(i, 1);
          continue;
        }

        const dx = to.x - from.x;
        const dy = to.y - from.y;
        const dist = Math.hypot(dx, dy) || 1;
        const ux = dx / dist;
        const uy = dy / dist;
        const sx = from.x + ux * NODE_PAD;
        const sy = from.y + uy * NODE_PAD;
        const ex = to.x - ux * NODE_PAD;
        const ey = to.y - uy * NODE_PAD;

        const tt = easeInOut(p.t);
        const hx = sx + (ex - sx) * tt;
        const hy = sy + (ey - sy) * tt;

        const beam = ctx.createLinearGradient(sx, sy, hx, hy);
        beam.addColorStop(0, "rgba(0, 229, 255, 0)");
        beam.addColorStop(0.4, "rgba(0, 229, 255, 0.08)");
        beam.addColorStop(0.85, "rgba(0, 229, 255, 0.45)");
        beam.addColorStop(1, "rgba(0, 229, 255, 0.85)");
        ctx.strokeStyle = beam;
        ctx.lineWidth = p.isHandoff ? 1.6 : 1.3;
        ctx.lineCap = "round";
        ctx.beginPath();
        ctx.moveTo(sx, sy);
        ctx.lineTo(hx, hy);
        ctx.stroke();

        const headR = p.isHandoff ? 10 : 8.5;
        const head = ctx.createRadialGradient(hx, hy, 0, hx, hy, headR);
        head.addColorStop(0, "rgba(230, 252, 255, 1)");
        head.addColorStop(0.35, "rgba(0, 229, 255, 0.6)");
        head.addColorStop(1, "rgba(0, 229, 255, 0)");
        ctx.fillStyle = head;
        ctx.beginPath();
        ctx.arc(hx, hy, headR, 0, Math.PI * 2);
        ctx.fill();
      }

      while (packets.length < MAX_PACKETS) {
        const idx = TEAM_INDICES[0][Math.floor(Math.random() * 4)];
        const p = buildHopFromIntra(idx, -1, 0);
        if (!p) break;
        packets.push(p);
      }

      ctx.restore();
    };

    const drawReticle = () => {
      const x = pointer.sx;
      const y = pointer.sy;
      ctx.save();
      ctx.globalCompositeOperation = "lighter";
      ctx.strokeStyle = "rgba(0, 229, 255, 0.45)";
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.arc(x, y, 24, 0, Math.PI * 2);
      ctx.stroke();
      ctx.save();
      ctx.translate(x, y);
      ctx.rotate(t * 0.0035);
      ctx.setLineDash([3, 5]);
      ctx.strokeStyle = "rgba(0, 229, 255, 0.6)";
      ctx.beginPath();
      ctx.arc(0, 0, 15, 0, Math.PI * 2);
      ctx.stroke();
      ctx.setLineDash([]);
      ctx.restore();
      ctx.strokeStyle = "rgba(0, 229, 255, 0.55)";
      ctx.beginPath();
      ctx.moveTo(x - 34, y); ctx.lineTo(x - 28, y);
      ctx.moveTo(x + 28, y); ctx.lineTo(x + 34, y);
      ctx.moveTo(x, y - 34); ctx.lineTo(x, y - 28);
      ctx.moveTo(x, y + 28); ctx.lineTo(x, y + 34);
      ctx.stroke();
      ctx.restore();
    };

    const step = () => {
      t += 1;
      pointer.sx += (pointer.x - pointer.sx) * 0.05;
      pointer.sy += (pointer.y - pointer.sy) * 0.05;

      ctx.globalCompositeOperation = "source-over";
      ctx.fillStyle = "rgba(17, 35, 50, 0.22)";
      ctx.fillRect(0, 0, width, height);

      const breathe = 0.5 + 0.5 * Math.sin(t * 0.0042);
      const coreR = Math.max(width, height) * (0.42 + breathe * 0.18);
      const cx = width * 0.5;
      const cy = height * 0.5;
      const core = ctx.createRadialGradient(cx, cy, 0, cx, cy, coreR);
      core.addColorStop(0, `rgba(27, 50, 69, ${0.5 + breathe * 0.18})`);
      core.addColorStop(0.55, "rgba(27, 50, 69, 0.1)");
      core.addColorStop(1, "rgba(17, 35, 50, 0)");
      ctx.fillStyle = core;
      ctx.fillRect(0, 0, width, height);

      drawBlobs();
      drawHUDRings();
      drawStreams();
      drawNetwork();
      if (pointer.active) drawReticle();

      ctx.globalCompositeOperation = "source-over";
      rafRef.current = requestAnimationFrame(step);
    };

    const onPointerMove = (e) => {
      const rect = canvas.getBoundingClientRect();
      pointer.x = e.clientX - rect.left;
      pointer.y = e.clientY - rect.top;
      pointer.active = true;
    };
    const onPointerLeave = () => {
      pointer.x = width / 2;
      pointer.y = height / 2;
    };
    const onResize = () => resize();

    resize();
    rafRef.current = requestAnimationFrame(step);
    window.addEventListener("resize", onResize);
    window.addEventListener("pointermove", onPointerMove, { passive: true });
    window.addEventListener("pointerleave", onPointerLeave);

    return () => {
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener("resize", onResize);
      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("pointerleave", onPointerLeave);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      data-testid="quantum-lightfield-canvas"
      aria-hidden="true"
      style={{
        position: "absolute",
        inset: 0,
        width: "100%",
        height: "100%",
        display: "block",
        background: "#112332",
      }}
    />
  );
};

export default QuantumLightfield;
